// src/components/AdminVendorView.js
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import API from "../services/api";
import { Container, Row, Col, Spinner, Alert, Tabs, Tab, Button, Modal } from "react-bootstrap";
import { BsWhatsapp } from "react-icons/bs";
import "./vendorView.css";
import { sendPortalWhatsApp } from "../utils/whatsapp";
import { formatVendorId } from "../utils/idFormat";
import AdminUserHistoryPanel from "./AdminUserHistoryPanel";
import PaymentHistoryPage from "./PaymentHistoryPage";

/* ---------- small utils ---------- */
const fmtIST = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—";
const orDash = (v) =>
  v === 0 ? "0" : (v == null || (typeof v === "string" && v.trim() === "") ? "—" : String(v));

/* File URL normalizer (absolute | leading / | filename) */
const toFileURL = (raw) => {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  if (v.startsWith("data:")) return v;              // data URI
  if (/^https?:\/\//i.test(v)) return v;            // absolute URL
  if (v.startsWith("/")) return v;                  // same-origin path
  return `/uploads/${v}`;                           // filename → /uploads/...
};

/* Make any URL absolute (needed by Google Viewer on relative paths) */
const absoluteURL = (u) => {
  if (!u) return "";
  if (u.startsWith("data:") || /^https?:\/\//i.test(u)) return u;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return u.startsWith("/") ? origin + u : origin + "/" + u.replace(/^\.?\//, "");
};

/* ---------- UI atoms ---------- */
const KV = ({ label, value, mono = false }) => (
  <div className="vx-kv">
    <div className="vx-kv-label">{label}</div>
    <div className={`vx-kv-value ${mono ? "mono" : ""}`}>{orDash(value)}</div>
  </div>
);

/* ==== Preview Modal (PDF inline with Google Viewer fallback) ==== */
const PreviewModal = ({ show, onHide, title, url }) => {
  const abs = absoluteURL(url || "");
  const directPDF = abs && (/\.pdf(\?|#|$)/i.test(abs) || abs.startsWith("data:application/pdf"));
  const src = directPDF
    ? `${abs}#toolbar=1&navpanes=0&scrollbar=1`
    : `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(abs)}`;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title || "Preview"}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ height: "80vh", padding: 0 }}>
        {abs ? (
          <iframe title="Document preview" src={src} style={{ width: "100%", height: "100%", border: 0 }} />
        ) : (
          <div className="p-4 text-muted">No preview available.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {abs && (
          <a className="btn btn-primary" href={abs} target="_blank" rel="noopener noreferrer" download>
            Download
          </a>
        )}
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

/* ==== Document tile (clean UI; no raw token links) ==== */
const DocRow = ({ label, val, onPreview }) => {
  // normalize => [{ name, url }]
  const normalize = (raw) => {
    if (!raw) return [];
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr
      .map((x) => {
        if (!x) return null;
        if (typeof x === "string") {
          return { name: (x.split("/").pop() || "file"), url: toFileURL(x) };
        }
        if (typeof x === "object") {
          const base = x.url || x.path || x.file || x.name || "";
          return {
            name: x.name || (base.split("/").pop() || "file"),
            url: toFileURL(base),
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const files = normalize(val);
  const has = files.length > 0;

  return (
    <div className={`vx-doc-tile ${has ? "has" : ""}`}>
      <div className="vx-doc-head">
        <div className="vx-doc-title">{label}</div>
        <div className={`vx-doc-dot ${has ? "ok" : ""}`} />
      </div>

      <div className="vx-doc-actions">
        {has ? (
          files.map((f, i) => (
            <div key={i} className="vx-doc-btns">
             
              <a className="btn btn-sm btn-outline-secondary" href={absoluteURL(f.url)} target="_blank" rel="noopener noreferrer" download>
                Download
              </a>
            </div>
          ))
        ) : (
          <div className="vx-doc-empty">—</div>
        )}
      </div>
    </div>
  );
};

export default function AdminVendorView({
  endpointBase = "/admin/vendors",
  endpointSuffix = "",
  listPath = "/admin/vendors",
  showAdminActions = true,
  showHistory = true,
  showLedger = false,
  historyEndpoint = "",
  paymentHistoryEndpoint = "",
  ledgerEndpoint = "",
}) {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState(null);
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [imgFail, setImgFail] = useState(false);

  // preview state
  const [preview, setPreview] = useState({ show: false, url: "", title: "" });
  const openPreview = (url, title) => setPreview({ show: true, url: absoluteURL(toFileURL(url)), title });
  const closePreview = () => setPreview({ show: false, url: "", title: "" });

  const load = async () => {
    setLoading(true);
    setNote(null);
    try {
      const res = await API.get(`${endpointBase}/${id}${endpointSuffix}`);
      setData(res.data);
      setImgFail(false);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to load vendor";
      setNote({ variant: "danger", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const rejectVendor = async () => {
    if (!data?._id) return;
    if (!window.confirm("Reject this vendor?")) return;
    try {
      setRejecting(true);
      await API.put(`/admin/vendors/${data._id}/reject`);
      setRejecting(false);
      nav(listPath);
    } catch (e) {
      setRejecting(false);
      setNote({ variant: "danger", text: "Reject failed" });
    }
  };

  const openVendorWhatsApp = async () => {
    const phone = p?.whatsappNumber || p?.contactMobile || data?.mobile || "";
    if (!phone) {
      window.alert("Vendor WhatsApp/mobile not available");
      return;
    }
    try {
      await sendPortalWhatsApp({
        to: phone,
        recipientName: data?.companyName || data?.fullName || "Vendor",
        message: `Hello ${data?.companyName || data?.fullName || "Vendor"}, this is from admin support.`,
        sourceModule: "vendor_profile",
        eventType: "manual_vendor_message",
        entityType: "Vendor",
        entityId: data?._id,
        isAdmin: true,
      });
      window.alert("WhatsApp sent");
    } catch (e) {
      window.alert(e?.response?.data?.message || "Failed to send WhatsApp");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) {
    return <Container className="py-5 text-center"><Spinner animation="border" /></Container>;
  }

  if (note) {
    return (
      <Container className="py-4">
        <Alert variant={note.variant}>{note.text}</Alert>
        <div className="d-flex gap-2">
          <button className="btn btn-secondary" onClick={() => (window.history.length > 1 ? nav(-1) : nav(listPath))}>Back</button>
          <Link to={listPath} className="btn btn-outline-secondary">Vendor List</Link>
        </div>
      </Container>
    );
  }

  if (!data) return null;

  const p = data.profileFull || {};
  const docs = p.documents || {};
  const bank = p.bank || {};
  const geo  = p.geoLocation || null;

  const productInterest = Array.isArray(p.productInterest) ? p.productInterest.join(", ") : p.productInterest;
  const vendorDisplayId = formatVendorId(p, data);
  const activeTab = searchParams.get("tab") || "basic";
  const effectiveHistoryEndpoint = historyEndpoint
    ? historyEndpoint.replace(":id", encodeURIComponent(id))
    : "";

  const initials = (data.companyName || data.fullName || "V")
    .split(" ").map((s) => s.charAt(0)).join("").slice(0, 2).toUpperCase();

  const rawLogo = p.logo || p.logoUrl || docs?.logo || data.logo || data.logoUrl || null;
  const logoUrl = toFileURL(rawLogo);

  return (
    <div className="vx vx-light">
      <Container className="py-3 vx-wrap">
        {/* ---- HERO ---- */}
        <div className="card-gradient mb-3">
          <div className="vx-hero">
            <div className="vx-hero-left">
              {logoUrl && !imgFail ? (
                <img src={logoUrl} alt="Vendor logo" className="vx-avatar vx-avatar-img" onError={() => setImgFail(true)} loading="lazy" />
              ) : (
                <div className="vx-avatar">{initials}</div>
              )}
              <div>
                <div className="vx-title clamp-1">{orDash(data.companyName)}</div>
                <div className="vx-subtitle clamp-1">{orDash(data.fullName)}</div>
                <div className="vx-chips">
                  <span className="vx-chip vx-chip-green"><span style={{ fontSize: 12 }}>●</span> {data.approved ? "Approved" : "Pending"}</span>
                  <span className="vx-chip vx-chip-blue">Cert: {data.certificateStatus || "pending"}</span>
                  {productInterest ? <span className="vx-chip vx-chip-purple clamp-1">{p.vendorType}</span> : null}
                </div>
              </div>
            </div>
            <div className="vx-hero-right">
              <div className="vx-meta mono">
                <div>ID: {vendorDisplayId}</div>
                <div>Created: {fmtIST(p.createdAt || data.createdAt)}</div>
                <div>Updated: {fmtIST(p.updatedAt || data.updatedAt)}</div>
              </div>
              <Button variant="success" size="sm" onClick={openVendorWhatsApp}>
                <span className="d-inline-flex align-items-center gap-1">
                  <BsWhatsapp />
                  <span>WhatsApp</span>
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* ---- BODY ---- */}
        <div className="card-glass">
          <Tabs
            activeKey={activeTab}
            onSelect={(key) => setSearchParams(key && key !== "basic" ? { tab: key } : {}, { replace: true })}
            className="mb-2"
          >
            <Tab eventKey="basic" title="Basic">
              <Row className="g-2">
                <Col md={6}>
                  <KV label="Full Name" value={data.fullName} />
                  <KV label="Mobile" value={data.mobile} />
                  <KV label="Email" value={data.email} />
                </Col>
                <Col md={6}>
                  <KV label="Company" value={data.companyName} />
                  <KV label="Vendor Type" value={p.vendorType || data.vendorType} />
                  <KV label="GST" value={p.gstNumber || data.gstNumber} mono />
                  <KV label="Annual Turnover" value={p.turnover} />
                </Col>

                <Col md={12}><div className="vx-divider" /></Col>

                <Col md={12}>
                  <KV
                    label="Address"
                    value={p.addressText || data.addressText || (typeof p.address === "string" ? p.address : "")}
                  />
                  {geo && Number.isFinite(parseFloat(geo.lat)) && Number.isFinite(parseFloat(geo.lng)) && (
                    <div className="mt-1">
                      <a href={`https://www.google.com/maps?q=${geo.lat},${geo.lng}`} target="_blank" rel="noreferrer" className="vx-link">
                        ◉ Open in Google Maps
                      </a>
                    </div>
                  )}
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="contact" title="Contact">
              <Row className="g-2">
                <Col md={6}>
                  <KV label="Contact Name" value={p.contactName} />
                  <KV label="Designation" value={p.contactDesignation} />
                  <KV label="Email" value={p.contactEmail} />
                  <KV label="Mobile" value={p.contactMobile} />
                </Col>
                <Col md={6}>
                  <KV label="WhatsApp" value={p.whatsappNumber} />
                  <KV label="Website" value={p.website} />
                  <KV label="LinkedIn" value={p.linkedin} />
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="business" title="Business & Ops">
              <Row className="g-2">
                <Col md={6}>
                  <KV label="Year Established" value={p.yearEstablished} />
                  <KV label="Employees" value={p.employeeCount} />
                  <KV label="Production Capacity" value={p.productionCapacity} />
                </Col>
                <Col md={6}>
                  <KV label="Factory Address" value={p.factoryAddress} />
                  <KV label="Payment Terms" value={p.paymentTerms} />
                  <KV label="Delivery Terms" value={p.deliveryTerms} />
                  <KV label="Past Clients" value={p.pastClients} />
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="legal" title="Legal & Compliance">
              <Row className="g-2">
                <Col md={6}>
                  <KV label="PAN" value={p.panNumber} mono />
                  <KV label="CIN" value={p.cinNumber} mono />
                  <KV label="MSME/Udyam" value={p.msmeNumber} mono />
                </Col>
                <Col md={6}>
                  <KV label="FSSAI" value={p.fssaiNumber} mono />
                  <KV label="IEC" value={p.iecNumber} mono />
                  <KV label="Certificate Issued" value={p.certificateIssued ? "Yes" : "No"} />
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="bank" title="Bank">
              <Row className="g-2">
                <Col md={6}>
                  <KV label="Account Name" value={bank.accountName} />
                  <KV label="Account Number" value={bank.accountNumber} mono />
                  <KV label="IFSC" value={bank.ifsc} mono />
                </Col>
                <Col md={6}>
                  <KV label="Bank Name" value={bank.bankName} />
                  <KV label="Branch" value={bank.branch} />
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="documents" title="Documents">
              <div className="vx-docs-grid">
                <DocRow label="GST Certificate"        val={docs.gstCertificate}         onPreview={openPreview} />
                <DocRow label="PAN Card"                val={docs.panCard}                onPreview={openPreview} />
                <DocRow label="MSME Certificate"        val={docs.msmeCertificate}        onPreview={openPreview} />
                <DocRow label="Cancelled Cheque"        val={docs.cancelledCheque}        onPreview={openPreview} />
                <DocRow label="ITR (Last 3 Years)"      val={docs.itrLast3Years || docs.brochure} onPreview={openPreview} />
                <DocRow label="Bank Statement (Last 3 Months)" val={docs.bankStatementLast3Months || docs.complianceCertificates} onPreview={openPreview} />
                <DocRow label="Recent Electricity Bill" val={docs.recentElectricityBill} onPreview={openPreview} />
              </div>
            </Tab>

            <Tab eventKey="meta" title="Meta">
              <KV label="Created" value={fmtIST(p.createdAt || data.createdAt)} />
              <KV label="Updated" value={fmtIST(p.updatedAt || data.updatedAt)} />
              <div className="vx-kv">
                <div className="vx-kv-label">User ID</div>
                <div className="vx-kv-value mono">{vendorDisplayId || "—"}</div>
              </div>
            </Tab>

            {showLedger ? (
              <Tab eventKey="ledger" title="Ledger & Payments">
                <PaymentHistoryPage
                  endpoint={paymentHistoryEndpoint || `/employee/vendors/${encodeURIComponent(id)}/payment-history`}
                  ledgerEndpoint={ledgerEndpoint || `/employee/vendors/${encodeURIComponent(id)}/ledger`}
                  title={`${data.companyName || data.fullName || "Vendor"} Ledger`}
                />
              </Tab>
            ) : null}

            {showHistory ? (
              <Tab eventKey="history" title="History">
                <AdminUserHistoryPanel role="vendor" userId={id} endpoint={effectiveHistoryEndpoint} />
              </Tab>
            ) : null}
          </Tabs>

          <div className="vx-sticky">
            <div className="d-flex gap-2">
              <Link to={listPath} className="btn btn-secondary">Close</Link>
              {showAdminActions && data.approved && (
                <Button variant="danger" disabled={rejecting} onClick={rejectVendor}>
                  {rejecting ? "Rejecting..." : "Reject"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Container>

      {/* PDF / Doc Preview */}
      <PreviewModal show={preview.show} onHide={closePreview} title={preview.title} url={preview.url} />
    </div>
  );
}
